/**
 * Okno „Nový import z Tandem Zostáv" (KOLO 128).
 *
 * Do KOLA 128 sa kusovník z programu ticho odložil medzi rozrobené
 * a user ho našiel len v plávajúcom pruhu — a aj to len vtedy, keď
 * program otvoril odkaz. Okno sa preto ukáže:
 *
 * - po otvorení odkazu `/zakazky?koncept=<id>` (aj po prihlásení),
 * - **a pri obyčajnom otvorení appky**, keď má user z programu import,
 *   ktorý ešte nevidel (`videny_at`). Nezáleží tak na tom, či program
 *   odkaz naozaj otvoril.
 *
 * „Neskôr" import len označí ako videný — ostáva v pruhu ako doteraz.
 *
 * Pri **existujúcej zákazke** (režim `update`) sa vyberá výpočet: zákazka
 * ich môže mať viac s pôvodom z TopSolidu (po KOLE 126 si ho nesú aj
 * duplikáty). Prednastavený je aktívny, ak pôvod má.
 *
 * **Časť výroby** (KOLO 203/208): export s iným dovetkom Určení
 * (`AA6262_obyvacka` po `AA6262_skrinka_prava`) je iná časť výroby tej istej
 * zákazky — okno predvolí **Pridať ako novú časť**. Do KOLA 203 ponúkalo len
 * aktualizáciu a dielce prvej časti zmizli. Zákazka, ktorá má len výpočty,
 * dostane prvú výrobu — výpočet sa exportom s AA neprepíše.
 *
 * **K výpočtu** (KOLO 208): nový export výroby (AA/Z), ktorého číslo ešte
 * nemá žiadna zákazka, sa dá pripojiť k výpočtu V — predvolený je výpočet
 * z toho istého výkresu (`navrh_vypoctu`).
 */
import { useEffect, useMemo, useState } from "react";
import { FileUp, Layers, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import {
  useKoncepty,
  useOznacVideny,
  type Koncept,
} from "@/api/hooks/useTopsolidKoncept";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import {
  VyberZakazky,
  type ZakazkaNaPripojenie,
} from "@/components/zakazka/PripojitDialog";

/** „1 dielec", „3 dielce", „286 dielcov". */
function pocet(n: number, jeden: string, dva: string, pat: string): string {
  if (n === 1) return `${n} ${jeden}`;
  if (n >= 2 && n <= 4) return `${n} ${dva}`;
  return `${n} ${pat}`;
}

/**
 * Číslo do nadpisu okna (KOLO 200) — rozpočet bez čísla z Tandem Zákazky
 * nemá AA: výpočet dostane V až pri importe, malá zákazka už má Z.
 */
export function cisloKonceptu(k: Koncept): string {
  if (k.rezim !== "update" && k.bez_cisla_typ === "neskor") return "bez čísla — výpočet";
  if (k.rezim !== "update" && k.bez_cisla_typ === "z" && k.cislo_zakazky) {
    return `${k.cislo_zakazky} · malá zákazka`;
  }
  return k.cislo_zakazky;
}

export function suhrn(k: Koncept): string {
  const casti: string[] = [];
  if (k.dielcov != null) casti.push(pocet(k.dielcov, "dielec", "dielce", "dielcov"));
  if (k.zdroj === "regal") {
    // Regál CRC (KOLO 232): výber zostáv je len rozsah aktualizácie — „13 z 15“ by klamalo
    if (k.zostav != null) casti.push(pocet(k.zostav, "zostava", "zostavy", "zostáv"));
  } else if (k.zostavy_vybrane && k.zostavy_vybrane.length > 0) {
    // Len časť zákazky (KOLO 175) — „z 39" povie, že ostatné sa nemenia
    const celkom = k.zostav_v_zakazke ? ` z ${k.zostav_v_zakazke}` : "";
    casti.push(`vybrané zostavy: ${k.zostavy_vybrane.length}${celkom}`);
  } else if (k.zostav != null) {
    casti.push(pocet(k.zostav, "zostava", "zostavy", "zostáv"));
  }
  return casti.join(" · ");
}

/** Čo user v okne zvolil (KOLO 203/208). */
export type VolbaImportu =
  | { typ: "update"; variantaId: string }
  | { typ: "cast"; nazov: string; doterajsiaNazov: string | null }
  | { typ: "novy"; kVypoctu: ZakazkaNaPripojenie | null };

/** Nový export výroby (AA/Z) — výpočet ho nemá; „AA neskôr" je výpočet sám. */
export function jeNovaVyroba(k: Koncept): boolean {
  return k.rezim !== "update" && k.bez_cisla_typ !== "neskor";
}

interface Volba {
  typ: "update" | "cast";
  variantaId: string | null;
  nazov: string;
  doterajsia: string;
  /** Zákazka má jednu výrobu mimo častí — stane sa prvou časťou. */
  prevod: boolean;
  /** Zákazka nemá výrobu — export pribudne ako prvá výroba vedľa výpočtov. */
  prvaVyroba: boolean;
  /** Zákazka sa načítala — dovtedy sa nevie, či treba prevod. */
  nacitane: boolean;
}

export function zaciatokVolby(k: Koncept): Volba {
  return {
    typ: k.cast_nova ? "cast" : "update",
    variantaId: k.varianta_id ?? null,
    nazov: k.cast ?? "",
    doterajsia: "",
    prevod: false,
    prvaVyroba: false,
    nacitane: false,
  };
}

export function volbaHotova(v: Volba): VolbaImportu | null {
  if (!v.nacitane) return null;
  if (v.typ === "update") return v.variantaId ? { typ: "update", variantaId: v.variantaId } : null;
  if (!v.nazov.trim()) return null;
  if (v.prevod && !v.doterajsia.trim()) return null;
  return {
    typ: "cast",
    nazov: v.nazov.trim(),
    doterajsiaNazov: v.prevod ? v.doterajsia.trim() : null,
  };
}

function VolbaCasti({
  koncept,
  volba,
  onZmena,
}: {
  koncept: Koncept;
  volba: Volba;
  onZmena: (v: Volba) => void;
}): JSX.Element {
  const detail = useZakazkaDetail(koncept.zakazka_id ?? undefined);
  const zakazka = detail.data?.data;
  const varianty = useMemo(() => zakazka?.varianty ?? [], [zakazka]);
  // Export výroby (zákazka už nie je výpočet V) mení len výrobu; výpočet V
  // z výkresu bez čísla sa aktualizuje ako výpočet (KOLO 208)
  const vypoctova = !!zakazka && zakazka.cislo_zakazky === zakazka.vypocet_cislo;
  const vyrobne = varianty.filter((v) => v.je_vyrobna);
  const poCastiach = vyrobne.some((v) => v.je_cast);
  const kandidati = useMemo(
    () =>
      varianty.filter(
        (v) =>
          v.ma_povod_z_topsolidu &&
          (vypoctova ? !v.je_vyrobna : v.je_vyrobna && (!poCastiach || v.je_cast)),
      ),
    [varianty, vypoctova, poCastiach],
  );
  const mimoCasti = vyrobne.filter((v) => !v.je_cast);
  const prevod = !vypoctova && mimoCasti.length === 1 ? (mimoCasti[0] ?? null) : null;
  const prvaVyroba = !vypoctova && vyrobne.length === 0;

  // Prednastav aktívnu, ak sa dá aktualizovať; inak prvú s pôvodom.
  // Názov doterajšej výroby ako časti = názov zákazky (dal ho prvý export).
  useEffect(() => {
    if (!zakazka) return;
    const aktivny = kandidati.find((v) => v.id === zakazka.aktivna_varianta_id);
    const variantaId =
      volba.variantaId && kandidati.some((v) => v.id === volba.variantaId)
        ? volba.variantaId
        : (aktivny ?? kandidati[0])?.id ?? null;
    onZmena({
      ...volba,
      // Výpočet sa exportom s AA neaktualizuje — len výroba pribudne
      typ: prvaVyroba ? "cast" : vypoctova ? "update" : volba.typ,
      nazov: volba.nazov || (prvaVyroba ? "Výroba" : ""),
      variantaId,
      doterajsia: volba.doterajsia || zakazka.nazov || prevod?.nazov || "",
      prevod: prevod !== null,
      prvaVyroba,
      nacitane: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zakazka]);

  if (detail.isPending) {
    return <p className="text-xs text-muted-foreground">Načítavam zákazku…</p>;
  }
  const co = vypoctova ? "výpočet" : poCastiach ? "časť výroby" : "výrobu";
  return (
    <fieldset className="space-y-2 text-sm">
      <legend className="sr-only">Čo s exportom</legend>
      {!vypoctova ? (
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name={`volba-${koncept.id}`}
            className="mt-1"
            checked={volba.typ === "cast"}
            onChange={() => onZmena({ ...volba, typ: "cast" })}
          />
          <span className="min-w-0 flex-1 space-y-1.5">
            <span className="block font-medium">
              {prvaVyroba ? "Pridať výrobu k výpočtom" : "Pridať ako novú časť výroby"}
            </span>
            {volba.typ === "cast" ? (
              <>
                <Input
                  aria-label={prvaVyroba ? "Názov výroby" : "Názov časti"}
                  className="h-8"
                  value={volba.nazov}
                  placeholder="napr. Obývačka"
                  onChange={(e) => onZmena({ ...volba, nazov: e.target.value })}
                />
                {prevod ? (
                  <span className="flex items-center gap-2 text-xs">
                    <span className="shrink-0 text-muted-foreground">
                      Doterajšia výroba {prevod.nazov} bude časť
                    </span>
                    <Input
                      aria-label="Názov doterajšej časti"
                      className="h-7 text-xs"
                      value={volba.doterajsia}
                      onChange={(e) => onZmena({ ...volba, doterajsia: e.target.value })}
                    />
                  </span>
                ) : null}
                <span className="block text-xs text-muted-foreground">
                  {prvaVyroba
                    ? "Výpočty ostanú bez zmeny — cena pre zákazníka sa nemení, výroba pribudne ako záložka."
                    : "Doterajšie časti ostanú bez zmeny, výroba spolu bude ich súčet."}
                </span>
              </>
            ) : null}
          </span>
        </label>
      ) : null}
      <label className="flex items-start gap-2">
        <input
          type="radio"
          name={`volba-${koncept.id}`}
          className="mt-1"
          checked={volba.typ === "update"}
          disabled={kandidati.length === 0}
          onChange={() => onZmena({ ...volba, typ: "update" })}
        />
        <span className="min-w-0 flex-1 space-y-1.5">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium">Aktualizovať {co}</span>
            {kandidati.length > 0 ? (
              <select
                aria-label="Varianta na aktualizáciu"
                className="h-8 rounded-md border bg-background px-2 text-sm"
                value={volba.variantaId ?? ""}
                onChange={(e) =>
                  onZmena({ ...volba, typ: "update", variantaId: e.target.value || null })
                }
              >
                {kandidati.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nazov}
                  </option>
                ))}
              </select>
            ) : null}
          </span>
          {kandidati.length === 0 ? (
            <span className="block text-xs text-muted-foreground">
              {prvaVyroba
                ? "Zákazka zatiaľ výrobu nemá — výpočet sa exportom s AA neprepíše."
                : "Žiadna výroba tejto zákazky nevznikla z TopSolidu — aktualizovať ju z exportu nejde."}
            </span>
          ) : volba.typ === "update" ? (
            <span className="block text-xs text-muted-foreground">
              Náhľad ukáže, čo pribudne, čo sa zmení a čo zmizne. Bez potvrdenia
              sa nič neprepíše.
            </span>
          ) : null}
        </span>
      </label>
    </fieldset>
  );
}

/** Nový export výroby — nová zákazka, alebo k výpočtu (KOLO 208). */
function VolbaVypoctu({
  koncept,
  kVypoctu,
  onZmena,
}: {
  koncept: Koncept;
  kVypoctu: ZakazkaNaPripojenie | null;
  onZmena: (z: ZakazkaNaPripojenie | null) => void;
}): JSX.Element {
  const [vyber, setVyber] = useState(false);
  return (
    <fieldset className="space-y-2 text-sm" data-testid="volba-vypoctu">
      <legend className="sr-only">Kam s výrobou</legend>
      <label className="flex items-start gap-2">
        <input
          type="radio"
          name={`vypocet-${koncept.id}`}
          className="mt-1"
          checked={kVypoctu === null}
          onChange={() => onZmena(null)}
        />
        <span className="font-medium">
          Nová zákazka {koncept.cislo_zakazky}
          <span className="block text-xs font-normal text-muted-foreground">
            Výpočet k nej pripojíš aj neskôr (⋮ → Pripojiť k výpočtu).
          </span>
        </span>
      </label>
      <label className="flex items-start gap-2">
        <input
          type="radio"
          name={`vypocet-${koncept.id}`}
          className="mt-1"
          checked={kVypoctu !== null}
          onChange={() =>
            koncept.navrh_vypoctu ? onZmena(koncept.navrh_vypoctu) : setVyber(true)
          }
        />
        <span className="min-w-0 flex-1 space-y-1.5">
          <span className="block font-medium">K výpočtu</span>
          {kVypoctu ? (
            <span className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full border border-primary bg-primary/10 px-2 py-0.5">
                <span className="font-mono">{kVypoctu.cislo_zakazky}</span> {kVypoctu.nazov ?? ""}
              </span>
              {koncept.navrh_vypoctu?.id === kVypoctu.id ? (
                <span className="text-muted-foreground">z toho istého výkresu</span>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs"
                onClick={() => setVyber((x) => !x)}
              >
                Zmeniť
              </Button>
            </span>
          ) : null}
          {vyber ? (
            <VyberZakazky
              druh="vypocet"
              okrem=""
              onVyber={(z) => {
                onZmena(z);
                setVyber(false);
              }}
            />
          ) : null}
          <span className="block text-xs text-muted-foreground">
            Výroba pribudne do výpočtu ako záložka, zákazka dostane číslo{" "}
            {koncept.cislo_zakazky}, výpočty ostanú.
          </span>
        </span>
      </label>
    </fieldset>
  );
}

export function NovyImportZostavy({
  ponuka,
  onPonukaHotova,
  importOtvoreny,
  aktivnyKoncept = null,
  onOtvor,
}: {
  /** Koncept z odkazu — ukáže sa, aj keď už bol videný. */
  ponuka: string | null;
  onPonukaHotova: () => void;
  /** Kým je otvorený sprievodca, okno sa neukazuje — prekrývali by sa. */
  importOtvoreny: boolean;
  /**
   * Koncept, na ktorom stojí sprievodca (aj zbalený). Opravený export ho
   * označí ako nevidený (KOLO 216), ale ponúkať ho netreba — sprievodca
   * si súbor načíta sám.
   */
  aktivnyKoncept?: string | null;
  onOtvor: (k: Koncept, volba: VolbaImportu | null) => void;
}): JSX.Element | null {
  const koncepty = useKoncepty();
  const oznac = useOznacVideny();
  const [volby, setVolby] = useState<Record<string, Volba>>({});
  // K výpočtu (KOLO 208) — `undefined` = ešte nezvolené, platí návrh
  const [vypocty, setVypocty] = useState<Record<string, ZakazkaNaPripojenie | null>>({});
  const [odlozene, setOdlozene] = useState<Set<string>>(new Set());

  // Odkaz môže prísť skôr, než cache pozná koncept, ktorý program práve
  // poslal — zoznam sa preto načíta nanovo.
  useEffect(() => {
    if (ponuka) void koncepty.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ponuka]);

  const zoznam = koncepty.data?.data ?? [];
  const kandidati = ponuka
    ? zoznam.filter((k) => k.id === ponuka)
    : zoznam.filter(
        (k) =>
          (k.zdroj === "zostavy" || k.zdroj === "regal") &&
          !k.videny_at &&
          !odlozene.has(k.id) &&
          k.id !== aktivnyKoncept,
      );

  // Odkaz na koncept, ktorý neexistuje (dokončený, zahodený, cudzí)
  useEffect(() => {
    if (!ponuka || koncepty.isFetching || !koncepty.data) return;
    if (!zoznam.some((k) => k.id === ponuka)) {
      onPonukaHotova();
      toast({
        variant: "destructive",
        title: "Rozrobený import sa nenašiel",
        description:
          "Mohol byť už naimportovaný alebo zahodený. Pošli kusovník z programu znova.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ponuka, koncepty.isFetching, koncepty.data]);

  if (importOtvoreny || kandidati.length === 0) return null;

  function oznacVidene(ids: string[]): void {
    // Lokálne hneď, aby sa okno nevrátilo skôr, než odpovie server
    setOdlozene((p) => new Set([...p, ...ids]));
    for (const id of ids) oznac.mutate(id);
  }

  function neskor(): void {
    oznacVidene(kandidati.map((k) => k.id));
    onPonukaHotova();
  }

  const volbaPre = (k: Koncept): Volba => volby[k.id] ?? zaciatokVolby(k);

  const vypocetPre = (k: Koncept): ZakazkaNaPripojenie | null =>
    k.id in vypocty ? (vypocty[k.id] ?? null) : (k.navrh_vypoctu ?? null);

  function otvor(k: Koncept): void {
    oznacVidene([k.id]);
    onPonukaHotova();
    // Diely Tandemu z regálu (KOLO 232) idú vždy do nábytku, ktorý určil server
    if (k.zdroj === "regal" && k.varianta_id) {
      onOtvor(k, { typ: "update", variantaId: k.varianta_id });
      return;
    }
    onOtvor(
      k,
      k.rezim === "update"
        ? volbaHotova(volbaPre(k))
        : jeNovaVyroba(k)
          ? { typ: "novy", kVypoctu: vypocetPre(k) }
          : null,
    );
  }

  const jeden = kandidati.length === 1 ? kandidati[0] : null;
  const regalJeden = jeden?.zdroj === "regal";
  const nadpis = regalJeden
    ? "Regál CRC — diely Tandemu treba naceniť"
    : jeden
    ? jeden.rezim === "update"
      ? volbaPre(jeden).typ === "cast"
        ? "Výroba z Tandem Zostáv"
        : "Aktualizácia z Tandem Zostáv"
      : "Nový import z Tandem Zostáv"
    : `Nové importy z Tandem Zostáv (${kandidati.length})`;

  return (
    <Dialog open onOpenChange={(o) => !o && neskor()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{nadpis}</DialogTitle>
          <DialogDescription>
            {regalJeden
              ? "Obchodník vložil regál do rozpočtu tovaru. Diely, ktoré vyrába Tandem, čakajú na nacenenie v nábytku."
              : "Program Tandem Zostavy poslal kusovník. Otvor náhľad teraz, alebo ho nechaj medzi rozrobenými importmi."}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-3">
          {kandidati.map((k) => {
            const regal = k.zdroj === "regal";
            const update = k.rezim === "update" && !regal;
            const volba = volbaPre(k);
            const nejde = regal ? !k.varianta_id : update && volbaHotova(volba) === null;
            return (
              <li
                key={k.id}
                className="space-y-2 rounded-md border p-3"
                data-testid="novy-import"
              >
                <div className="flex items-start gap-3">
                  {update && volba.typ === "cast" ? (
                    <Layers className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  ) : update ? (
                    <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  ) : (
                    <FileUp className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  )}
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="font-medium">
                      {[cisloKonceptu(k), k.nazov].filter(Boolean).join(" ") ||
                        k.nazov_suboru}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {suhrn(k) || k.nazov_suboru}
                    </div>
                    {k.spojena_s ? (
                      // Číslo zmizlo spojením výrob (KOLO 209) — výkres ho nesie ďalej
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400" role="note">
                        {k.cislo_zakazky} je spojené so zákazkou {k.spojena_s.cislo_zakazky}
                        {k.spojena_s.nazov ? ` (${k.spojena_s.nazov})` : ""} — pošli výkres pod{" "}
                        {k.spojena_s.cislo_zakazky} s dovetkom, tento import založí novú
                        zákazku {k.cislo_zakazky}.
                      </p>
                    ) : null}
                    {regal ? (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid="regal-koncept">
                        Regál CRC od {k.autor || "obchodníka"} — diely Tandemu treba naceniť.
                        Pribudnú do nábytku ako nové zostavy, ostatné zostavy sa nezmenia.
                      </p>
                    ) : null}
                    {update ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {k.cast_nova
                          ? `Zákazka už existuje — podľa Určení je to nová výroba${
                              k.cast ? ` („${k.cast}“)` : ""
                            }.`
                          : "Zákazka už existuje."}
                      </p>
                    ) : null}
                  </div>
                </div>
                {update ? (
                  <VolbaCasti
                    koncept={k}
                    volba={volba}
                    onZmena={(v) => setVolby((p) => ({ ...p, [k.id]: v }))}
                  />
                ) : jeNovaVyroba(k) ? (
                  <VolbaVypoctu
                    koncept={k}
                    kVypoctu={vypocetPre(k)}
                    onZmena={(z) => setVypocty((p) => ({ ...p, [k.id]: z }))}
                  />
                ) : null}
                <div className="flex justify-end">
                  <Button size="sm" disabled={nejde} onClick={() => otvor(k)}>
                    Otvoriť náhľad
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={neskor}>
            Neskôr
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
